import { useEffect, useState } from "react";
import { Check, Clock, Eye, Globe, Lock, Share2 } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { Popover, PopoverContent, PopoverTrigger } from "../primitives/popover";
import {
  readPreviewContext,
  type PreviewContext,
  type PreviewDataMode,
} from "./previewContext";
import { currentTime } from "@shared-core/domain/current-time";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { PreviewClock } from "@shared-core/data/journey-testing-types";
import {
  formatClockOffset,
  formatPreviewTime,
  usePreviewClock,
} from "@shared-core/domain/preview-clock";
import { paths } from "@shared-core/routing/routes";

/**
 * PreviewBanner — a full-width amber bar pinned above the logo and nav that
 * makes it unmistakable the running instance is a cis-preview, NOT the live app.
 *
 * Renders nothing outside a preview environment (readPreviewContext() returns
 * null unless VITE_PREVIEW === "1"). Mounted at the app root above the router so
 * it spans the full width and sits over the chrome.
 *
 * The "copy share link" control copies the mode-specific share URL via the
 * clipboard capability port (never touching navigator directly) and explains the
 * per-mode sharing rules in a popover. It hides entirely when no share URL is
 * available (e.g. PUBLIC_DOMAIN was unset at provision time).
 *
 * In a testing preview it also shows the preview's clock ("Preview time: Tue 14 Oct 09:12 · +7 days"), linking to
 * the test console where it is moved on (quote-to-invoice-e2e-testing 07 §1). `clock` is injectable for stories.
 */
export function PreviewBanner({
  context = readPreviewContext(),
  clock,
}: {
  context?: PreviewContext | null;
  clock?: PreviewClock | null;
}) {
  if (!context) return null;
  return context.testing && clock === undefined ? (
    <TestingPreviewBannerBar context={context} />
  ) : (
    <PreviewBannerBar context={context} clock={clock ?? null} />
  );
}

function TestingPreviewBannerBar({ context }: { context: PreviewContext }) {
  const adapter = useDataAdapter();
  const clock = usePreviewClock(adapter, true);
  return <PreviewBannerBar context={context} clock={clock} />;
}

const MODE_COPY: Record<PreviewDataMode, string> = {
  full_stack:
    "Pollards login required to access. This full-stack preview runs on a copy of live data. Share the preview inside Pollards only, never with third parties.",
  mock: "Anyone with this link can access. This mock preview runs on fake data. Share the preview inside Pollards only, never with third parties.",
};

function PreviewBannerBar({
  context,
  clock,
}: {
  context: PreviewContext;
  clock: PreviewClock | null;
}) {
  const isFullStack = context.dataMode === "full_stack";
  const ModeIcon = isFullStack ? Lock : Globe;
  const modeLabel = isFullStack
    ? "full-stack, copy of live data"
    : "mock, fake data";

  return (
    <div
      role="status"
      className="w-full border-b border-amber-300 bg-amber-100 text-amber-950 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-200/70 px-2 py-0.5 text-xs font-medium dark:border-amber-700 dark:bg-amber-900/60">
          <Eye className="size-3.5" />
          {context.testing ? "Testing preview" : "Preview"}
        </span>

        <div className="min-w-0 leading-tight">
          <p className="text-sm font-semibold">
            Preview environment — not live
          </p>
          <p className="inline-flex items-center gap-1.5 text-xs text-amber-800 dark:text-amber-200/80">
            branch <code className="font-mono">{context.branch}</code> ·
            <ModeIcon className="size-3.5" />
            {modeLabel}
          </p>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {clock && (
            <a
              href={paths.journeyTestingHome()}
              className="inline-flex items-center gap-1.5 text-xs font-medium underline-offset-2 hover:underline"
              data-testid="preview-clock"
            >
              <Clock className="size-3.5" />
              Preview time: {formatPreviewTime(clock.now)} ·{" "}
              {formatClockOffset(clock.offset_seconds)}
            </a>
          )}
          <ExpiryCountdown expiresAt={context.expiresAt} />
          {context.shareUrl && (
            <CopyShareLink
              shareUrl={context.shareUrl}
              dataMode={context.dataMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ExpiryCountdown({ expiresAt }: { expiresAt: Date | null }) {
  const capabilities = usePlatformCapabilities();
  const [remaining, setRemaining] = useState<string | null>(() =>
    formatRemaining(expiresAt),
  );

  useEffect(() => {
    if (!expiresAt) {
      setRemaining(null);
      return;
    }
    setRemaining(formatRemaining(expiresAt));
    // Tick each minute (visibility-aware) so the countdown stays fresh without
    // a per-second re-render.
    return capabilities.setVisibilityAwareInterval(() => {
      setRemaining(formatRemaining(expiresAt));
    }, 60_000);
  }, [expiresAt, capabilities]);

  if (!remaining) return null;
  return (
    <span className="hidden text-xs font-medium text-amber-800 sm:inline dark:text-amber-200/80">
      {remaining}
    </span>
  );
}

function CopyShareLink({
  shareUrl,
  dataMode,
}: {
  shareUrl: string;
  dataMode: PreviewDataMode;
}) {
  const capabilities = usePlatformCapabilities();
  const [open, setOpen] = useState(false);

  const handleCopy = () => {
    // Copy via the capability port — shell code never touches navigator here.
    void capabilities.copyToClipboard(shareUrl);
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-amber-200/70 px-2.5 py-1 text-xs font-medium text-amber-950 transition-colors hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/60 dark:text-amber-100 dark:hover:bg-amber-900"
        >
          <Share2 className="size-3.5" />
          Copy share link
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="space-y-2">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <Check className="size-4 text-green-600 dark:text-green-500" />
            Share link copied
          </p>
          <p className="text-xs text-muted-foreground">{MODE_COPY[dataMode]}</p>
          <p className="select-all break-all rounded bg-muted px-2 py-1 font-mono text-xs text-foreground">
            {shareUrl}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatRemaining(
  expiresAt: Date | null,
  now: Date = currentTime(),
): string | null {
  if (!expiresAt) return null;
  const ms = expiresAt.getTime() - now.getTime();
  if (ms <= 0) return "Expired";
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  return `Expires in ${parts}`;
}
